import Collapse from "react-bootstrap/Collapse";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { isNil, isNull, isEmpty } from "lodash";
import { BsChevronDown } from "react-icons/bs";
import { getSalesData } from "@/services/PropertyService";
import { UUID } from "crypto";

interface SalesInfoProps {
    property_id: UUID;
    propertyStatus?: string;
}

const SalesInfo: React.FC<SalesInfoProps> = ({ property_id, propertyStatus }) => {
    const [openSalesInfo, setOpenSalesInfo] = useState(false);

    // Reset the open sales info flag on property change
    useEffect(() => {
        setOpenSalesInfo(false);
    }, [property_id]);

    /**
     * Fetches sales data for a specific property using the 'getSalesData' function.
     * This query is used in React applications to manage and cache remote data fetching.
     *
     * @param property - The property for which sales data is to be fetched.
     * @param property_id - The unique identifier of the property.
     *
     * @returns The fetched sales data for the property, or an empty object if no data is available.
     */
    const currentPropertySalesData = useQuery({
        queryKey: ["current_property_sales_data", property_id],
        queryFn: () => getSalesData(property_id),
        enabled: !isNil(property_id) && !isNull(property_id) && !isEmpty(property_id),
        placeholderData: () => {
            return {} as any;
        },
    });

    /**
     * Calculates the updated date for a property based on sales data, or returns an empty string if the data is not available.
     */
    const updatedDate =
        !currentPropertySalesData.isFetching &&
        !(currentPropertySalesData as any).isPlaceholderData &&
        !isNil((currentPropertySalesData as any)?.data?.sales_data?.property?.updated_at)
            ? new Date((currentPropertySalesData as any).data.sales_data.property.updated_at).toLocaleDateString()
            : "";

    /**
     * Trims the listing ID obtained from sales data or returns an empty string if the data is not available.
     */
    const trimmedListingID =
        !currentPropertySalesData.isFetching &&
        !(currentPropertySalesData as any).isPlaceholderData &&
        !isNil((currentPropertySalesData as any)?.data?.sales_data?.listingId)
            ? (currentPropertySalesData as any).data.sales_data.listingId.replace(/\D/g, "")
            : "";

    const salesData = (currentPropertySalesData as any)?.data?.sales_data;
    const hasOriginatingSystemName = !!salesData?.originatingSystemName;

    // Chevron icon component with rotation
    const ChevronIcon = () => (
        <BsChevronDown
            style={{
                width: "16px",
                height: "16px",
                color: "#3b166a",
                transform: openSalesInfo ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
            }}
        />
    );

    // Only show the Sales Info section when the listing comes from SimplyRETS.
    if (!hasOriginatingSystemName) {
        return null;
    }

    return (
        <>
            <Collapse in={openSalesInfo}>
                <div id="additional-details">
                    <div className="py-3">
                        {!currentPropertySalesData.isFetching &&
                        !(currentPropertySalesData as any).isPlaceholderData &&
                        !isNil((currentPropertySalesData as any).data) &&
                        !isEmpty((currentPropertySalesData as any).data) ? (
                            <>
                                <h3 className="mb-2">
                                    <strong className="subtitle">Sales Info</strong>
                                </h3>

                                <div className="details-item d-flex justify-content-between">
                                    <span>Source:</span>
                                    <p>
                                        {salesData?.originatingSystemName} as Distributed by MLS Grid
                                        {!isEmpty(trimmedListingID) && ` #${trimmedListingID}`}
                                    </p>
                                </div>

                                <div className="details-item d-flex justify-content-between">
                                    <span>Listing agent:</span>
                                    <p>
                                        {salesData?.listingAgent?.firstName || ""}{" "}
                                        {salesData?.listingAgent?.lastName || ""},{" "}
                                        {salesData?.listingAgent?.brokerage || ""} (
                                        {salesData?.listingAgent?.phoneNumber || ""})
                                    </p>
                                </div>

                                {propertyStatus === "Closed" && (
                                    <div className="details-item d-flex justify-content-between">
                                        <span>Buyer's agent:</span>
                                        <p>
                                            {salesData?.buyerAgent?.firstName || " "}
                                            {salesData?.buyerAgent?.lastName || ""},{" "}
                                            {salesData?.buyerAgent?.brokerage || ""}
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <p className="sales-info-disclaimer">
                                        Details provided by {salesData?.originatingSystemName || ""} as Distributed by
                                        MLS Grid and may not match the public record. Based on information submitted to
                                        the MLS GRID as of {updatedDate} as licensed by Mike McElroy of Kale Realty. All
                                        data is obtained from various sources and may not have been verified by broker
                                        or MLS GRID. Supplied Open House Information is subject to change without
                                        notice. All information should be independently reviewed and verified for
                                        accuracy. Properties may or may not be listed by the office/agent presenting the
                                        information. Properties displayed may be listed or sold by various participants
                                        in the MLS. Some IDX listings have been excluded from this website.
                                    </p>
                                </div>
                            </>
                        ) : (
                            <div>
                                <p>Sales data not found.</p>
                            </div>
                        )}
                    </div>
                </div>
            </Collapse>

            <button
                id="toggle-details"
                data-cy="share-button"
                className="toggle-sales-info tw-mx-auto tw-flex tw-items-center tw-justify-start tw-gap-2 tw-px-4 tw-py-2 tw-rounded-lg tw-border-none tw-text-[#3b166a] tw-bg-transparent tw-font-['Noto_Sans'] tw-text-base tw-font-medium tw-cursor-pointer tw-transition-all tw-duration-200 tw-ease-in-out tw-text-left tw-w-full"
                onClick={() => setOpenSalesInfo(!openSalesInfo)}
                disabled={currentPropertySalesData.isFetching && (currentPropertySalesData as any).isPlaceholderData}
            >
                <ChevronIcon />
                {openSalesInfo ? "Hide Sales Information" : "More Sales Information"}
            </button>
        </>
    );
};

export default SalesInfo;
